import React from 'react';
import { Mail, Phone, User } from 'lucide-react';

const ModernTemplate = ({ quote }) => {
  if (!quote) {
    return null;
  }

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // Parse date in local timezone to avoid UTC offset issues
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header Section */}
      <div className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            {quote.quoteTitle || 'Travel Quote'}
          </h1>
          <div className="flex flex-wrap gap-4 text-sm text-slate-600">
            {(quote.client?.firstName || quote.client?.lastName) && (
              <div className="flex items-center gap-2">
                <span className="font-medium">Client:</span>
                <span>{quote.client.firstName} {quote.client.lastName}</span>
              </div>
            )}
            {quote.travelDates?.start && (
              <div className="flex items-center gap-2">
                <span className="font-medium">Travel Dates:</span>
                <span>
                  {formatDate(quote.travelDates.start)}
                  {quote.travelDates.end && ` - ${formatDate(quote.travelDates.end)}`}
                </span>
              </div>
            )}
            {(quote.travelers?.adults || quote.travelers?.children?.length) && (
              <div className="flex items-center gap-2">
                <span className="font-medium">Travelers:</span>
                <span>
                  {quote.travelers.adults || 0} Adult{quote.travelers.adults !== 1 ? 's' : ''}
                  {quote.travelers.children?.length > 0 && `, ${quote.travelers.children.length} Child${quote.travelers.children.length !== 1 ? 'ren' : ''}`}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Components Section */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        <h2 className="text-2xl font-bold text-slate-900 mb-6">Trip Components</h2>
        
        {quote.components && quote.components.length > 0 ? (
          <div className="space-y-4">
            {quote.components.map((component, index) => (
              <div
                key={component.id || index}
                className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-xl font-semibold text-slate-900">
                    {component.title || `Component ${index + 1}`}
                  </h3>
                  <span className="text-lg font-bold text-blue-600">
                    {formatCurrency(component.price)}
                  </span>
                </div>
                
                <div className="text-sm text-slate-600 mb-2">
                  <span className="inline-block bg-slate-100 px-3 py-1 rounded-full">
                    {component.type || 'General'}
                  </span>
                </div>

                {component.data && Object.keys(component.data).length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      {Object.entries(component.data).map(([key, value]) => {
                        if (!value || key === 'isOption' || key === 'optionDescription') return null;
                        
                        // Check if this is a price field and format accordingly
                        const isPriceField = key.toLowerCase().includes('price') || 
                                             key.toLowerCase().includes('cost') || 
                                             key.toLowerCase().includes('fee') ||
                                             key.toLowerCase().includes('total') ||
                                             key.toLowerCase().includes('deposit');
                        const isNumeric = !isNaN(parseFloat(value)) && isFinite(value);
                        const displayValue = isPriceField && isNumeric ? formatCurrency(parseFloat(value)) : String(value);
                        
                        return (
                          <div key={key} className="flex flex-col">
                            <span className="font-medium text-slate-700 capitalize">
                              {key.replace(/_/g, ' ')}:
                            </span>
                            <span className="text-slate-600">{displayValue}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Component Photos */}
                {component.photos && component.photos.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-slate-200">
                    <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <span>📸</span> Photos
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {component.photos.map((photo, photoIndex) => (
                        <div 
                          key={photoIndex} 
                          className="relative group cursor-pointer rounded-lg overflow-hidden shadow hover:shadow-md transition-shadow border border-slate-200 bg-white"
                          onClick={() => window.open(photo.url, '_blank')}
                        >
                          <img 
                            src={photo.url} 
                            alt={photo.name || 'Component photo'} 
                            className="w-full h-auto object-contain"
                            style={{ maxHeight: '300px' }}
                          />
                          <div className="absolute inset-0 bg-slate-900/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <span className="text-white text-sm font-medium">{photo.name || 'View Photo'}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Component Documents */}
                {component.documents && component.documents.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
                      <span>📋</span> Documents
                    </h4>
                    <div className="space-y-2">
                      {component.documents.map((doc, docIndex) => (
                        <a 
                          key={docIndex}
                          href={doc.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 bg-slate-50 hover:bg-slate-100 px-4 py-2 rounded-lg transition-colors border border-slate-200"
                        >
                          <span>📄</span>
                          <span className="text-slate-900 text-sm font-medium">{doc.name}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 text-center text-slate-500">
            No components added yet
          </div>
        )}

        {/* Pricing Summary */}
        <div className="mt-8 bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <h3 className="text-xl font-bold text-slate-900 mb-4">Pricing Summary</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center text-lg">
              <span className="font-medium text-slate-700">Total Amount:</span>
              <span className="font-bold text-slate-900">
                {formatCurrency(quote.totalPrice)}
              </span>
            </div>
            {quote.depositAmount > 0 && (
              <div className="flex justify-between items-center pt-3 border-t border-slate-200">
                <span className="font-medium text-slate-700">Deposit Due:</span>
                <span className="font-bold text-blue-600">
                  {formatCurrency(quote.depositAmount)}
                </span>
              </div>
            )}
            {quote.depositDueDate && (
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-600">Deposit Due Date:</span>
                <span className="text-slate-700">{formatDate(quote.depositDueDate)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Notes Section */}
        {quote.notes && (
          <div className="mt-6 bg-blue-50 rounded-lg border border-blue-200 p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">Additional Notes</h3>
            <p className="text-blue-800 whitespace-pre-wrap">{quote.notes}</p>
          </div>
        )}

        {/* Agent Contact Footer */}
        {(quote.agent_name || quote.agent_email || quote.agent_phone) && (
          <div className="mt-8 bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-center gap-2 mb-4">
              <User className="w-5 h-5 text-slate-600" />
              <h3 className="text-lg font-semibold text-slate-900">Your Travel Advisor</h3>
            </div>
            
            {quote.agent_name && (
              <div className="text-center mb-4">
                <p className="text-xl font-bold text-slate-900">{quote.agent_name}</p>
                {quote.agent_business_name && (
                  <p className="text-slate-600 font-medium">{quote.agent_business_name}</p>
                )}
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              {quote.agent_email && (
                <a 
                  href={`mailto:${quote.agent_email}`}
                  className="flex items-center gap-2 text-slate-700 hover:text-blue-600 transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  <span className="text-sm font-medium">{quote.agent_email}</span>
                </a>
              )}
              
              {quote.agent_email && quote.agent_phone && (
                <span className="hidden sm:block text-slate-300">|</span>
              )}
              
              {quote.agent_phone && (
                <a 
                  href={`tel:${quote.agent_phone}`}
                  className="flex items-center gap-2 text-slate-700 hover:text-blue-600 transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  <span className="text-sm font-medium">{quote.agent_phone}</span>
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModernTemplate;
